import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLiveTVMiniPlayer } from '../context/LiveTVMiniPlayerContext';
import { X, Maximize2, Minimize2, Volume2, VolumeX, Tv, Play, ExternalLink, Copy, Check } from 'lucide-react';
import Hls from 'hls.js';

const LiveTVMiniPlayer = () => {
  const {
    liveChannel,
    isMinimized,
    isMuted,
    minimizePlayer,
    expandPlayer,
    closePlayer,
    toggleMute,
  } = useLiveTVMiniPlayer();

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [streamError, setStreamError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef(null);

  // Auto-hide controls after mouse stops moving (expanded mode)
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 800);
  }, []);

  // Clear timer on unmount or when minimized
  useEffect(() => {
    if (isMinimized) {
      clearTimeout(hideTimerRef.current);
      setShowControls(true);
    }
    return () => clearTimeout(hideTimerRef.current);
  }, [isMinimized]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = playerContainerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen?.() || container.webkitRequestFullscreen?.();
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.();
    }
  }, []);

  // Setup HLS player - only when channel changes
  useEffect(() => {
    if (!liveChannel?.id || !videoRef.current) return;

    const video = videoRef.current;

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Use proxy URL to avoid CORS
    const proxyUrl = `/api/iptv/proxy/${liveChannel.id}.m3u8`;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Increase retry attempts for better resilience
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 6,
        levelLoadingMaxRetry: 6,
        // Retry delay settings
        fragLoadingRetryDelay: 1000,
        manifestLoadingRetryDelay: 1000,
        levelLoadingRetryDelay: 1000,
        // Timeout settings
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 20000,
        levelLoadingTimeOut: 20000,
        // Live stream settings
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        liveDurationInfinity: true,
        // Start from live edge
        startPosition: -1,
      });

      hls.loadSource(proxyUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(e => console.log('Autoplay prevented:', e));
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.log('HLS error:', data.type, data.details, data.fatal);
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // Try to recover from network errors
              console.log('Network error, attempting recovery...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              // Try to recover from media errors
              console.log('Media error, attempting recovery...');
              hls.recoverMediaError();
              break;
            default:
              // Cannot recover, show error
              if (data.details === 'bufferAddCodecError') {
                setStreamError('This stream uses a codec not supported by browsers (HEVC/H.265).');
              } else {
                setStreamError('Failed to load stream. Try using VLC or IBO Player.');
              }
              break;
          }
        }
      });

      // Handle stall recovery
      hls.on(Hls.Events.BUFFER_STALLED_ERROR, () => {
        console.log('Buffer stalled, attempting to recover...');
        hls.startLoad();
      });

      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = proxyUrl;
      video.play().catch(e => console.log('Autoplay prevented:', e));
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [liveChannel?.id]);

  // Update mute state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Copy URL handler
  const handleCopyUrl = () => {
    if (liveChannel?.url) {
      navigator.clipboard.writeText(liveChannel.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Don't render if no channel
  if (!liveChannel) {
    return null;
  }

  // Use a single render structure with conditional styling
  // This keeps the video element in the DOM at all times
  return (
    <>
      {/* Backdrop for expanded view */}
      {!isMinimized && (
        <div 
          className="fixed inset-0 z-[99998] bg-black/95"
          onClick={minimizePlayer}
        />
      )}

      {/* Player container - styling changes based on isMinimized */}
      <div 
        ref={playerContainerRef}
        onMouseMove={!isMinimized ? handleMouseMove : undefined}
        onMouseLeave={!isMinimized ? handleMouseLeave : undefined}
        style={!isMinimized && !showControls ? { cursor: 'none' } : undefined}
        className={`fixed z-[99999] bg-black overflow-hidden transition-all duration-300 ${
          isMinimized 
            ? 'bottom-4 right-4 w-80 rounded-xl border border-white/20 shadow-2xl hover:shadow-[0_0_30px_rgba(6,182,212,0.2)] group'
            : isFullscreen
              ? 'inset-0 rounded-none'
              : 'inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-5xl rounded-2xl border border-white/20'
        }`}
        data-testid="live-tv-mini-player"
      >
        {/* Header - only visible in expanded mode, auto-hides */}
        {!isMinimized && (
          <div className={`absolute top-0 left-0 right-0 z-30 p-4 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex items-center gap-3">
              {liveChannel.logo && (
                <img 
                  src={`/api/iptv/image-proxy?url=${encodeURIComponent(liveChannel.logo)}`}
                  alt={liveChannel.name}
                  className="w-10 h-10 object-contain bg-white/10 rounded-lg p-1"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded animate-pulse">LIVE</span>
                  <h3 className="text-white font-bold">{liveChannel.name}</h3>
                </div>
                <p className="text-gray-400 text-sm">{liveChannel.category}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={minimizePlayer}
                className="p-2 bg-white/10 rounded-full hover:bg-cyan-500/30 transition-colors"
                title="Minimize to corner"
                data-testid="minimize-player-btn"
              >
                <Minimize2 size={20} className="text-white" />
              </button>
              <button
                onClick={closePlayer}
                className="p-2 bg-white/10 rounded-full hover:bg-red-500/30 transition-colors"
                title="Close"
                data-testid="close-expanded-player-btn"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
          </div>
        )}

        {/* Video Container */}
        <div className={`relative bg-black ${isMinimized ? 'aspect-video' : isFullscreen ? 'w-full h-full' : 'aspect-video'}`}>
          {/* Single video element that persists - NO native controls */}
          <video
            ref={videoRef}
            autoPlay
            muted={isMuted}
            className={`w-full h-full ${isMinimized ? 'object-cover' : 'object-contain'}`}
            playsInline
            style={!isMinimized ? { pointerEvents: 'none' } : undefined}
          />

          {/* LIVE Badge - only in minimized mode */}
          {isMinimized && (
            <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded animate-pulse z-10">
              LIVE
            </div>
          )}

          {/* Mini player controls overlay - only in minimized mode, show on hover */}
          {isMinimized && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30">
              {/* Top Controls */}
              <div className="absolute top-2 right-2 flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); expandPlayer(); }}
                  className="p-1.5 bg-black/70 rounded-lg hover:bg-cyan-500/50 transition-colors cursor-pointer"
                  title="Expand"
                  data-testid="expand-player-btn"
                >
                  <Maximize2 size={14} className="text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); closePlayer(); }}
                  className="p-1.5 bg-black/70 rounded-lg hover:bg-red-500/80 transition-colors cursor-pointer"
                  title="Close"
                  data-testid="close-mini-player-btn"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>

              {/* Center Play Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <button
                  onClick={(e) => { e.stopPropagation(); expandPlayer(); }}
                  className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors pointer-events-auto cursor-pointer"
                >
                  <Play size={24} className="text-white ml-0.5" fill="white" />
                </button>
              </div>

              {/* Bottom Controls */}
              <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                  className="p-1.5 bg-black/70 rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX size={14} className="text-white" /> : <Volume2 size={14} className="text-white" />}
                </button>
              </div>
            </div>
          )}

          {/* Stream Error Overlay */}
          {streamError && (
            <div className={`absolute inset-0 flex items-center justify-center z-20 ${isMinimized ? 'bg-black/80' : 'bg-black/90'}`}>
              <div className={`text-center ${isMinimized ? 'p-2' : 'p-6 max-w-md'}`}>
                <Tv size={isMinimized ? 24 : 48} className={`mx-auto mb-${isMinimized ? '1' : '4'} ${isMinimized ? 'text-gray-500' : 'text-cyan-500'}`} />
                {isMinimized ? (
                  <>
                    <p className="text-gray-400 text-xs">Codec not supported</p>
                    <button
                      onClick={handleCopyUrl}
                      className="mt-2 px-2 py-1 bg-cyan-600 rounded text-white text-xs hover:bg-cyan-700"
                    >
                      {copied ? 'Copied!' : 'Copy URL'}
                    </button>
                  </>
                ) : (
                  <>
                    <h4 className="text-white font-semibold mb-2">Stream Not Compatible</h4>
                    <p className="text-gray-400 mb-4 text-sm">{streamError}</p>
                    <div className="flex flex-col gap-3">
                      <button 
                        onClick={handleCopyUrl}
                        className="px-4 py-2 bg-cyan-600 rounded-lg text-white hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2"
                      >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Copied!' : 'Copy URL for IBO Player'}
                      </button>
                      <a 
                        href={`vlc://${liveChannel.url}`}
                        className="px-4 py-2 bg-orange-600 rounded-lg text-white hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <ExternalLink size={18} />
                        Open in VLC
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Controls - only in expanded mode, auto-hides */}
        {!isMinimized && (
          <div className={`absolute bottom-0 left-0 right-0 z-30 px-4 py-3 bg-gradient-to-t from-black via-black/80 to-transparent flex items-center justify-between transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                title={isMuted ? 'Unmute' : 'Mute'}
                data-testid="expanded-mute-btn"
              >
                {isMuted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
              </button>
              <span className="text-gray-400 text-sm">Live TV &bull; {liveChannel.category}</span>
            </div>
            <button
              onClick={toggleFullscreen}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors flex items-center gap-2"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              data-testid="livetv-fullscreen-btn"
            >
              {isFullscreen ? <Minimize2 size={20} className="text-white" /> : <Maximize2 size={20} className="text-white" />}
              <span className="text-white text-sm font-medium">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
            </button>
          </div>
        )}

        {/* Info Bar - only in minimized mode */}
        {isMinimized && (
          <div className="p-2 bg-gray-900/95 border-t border-white/10">
            <div className="flex items-center gap-2">
              {liveChannel.logo && (
                <img 
                  src={`/api/iptv/image-proxy?url=${encodeURIComponent(liveChannel.logo)}`}
                  alt={liveChannel.name}
                  className="w-8 h-8 object-contain bg-white/10 rounded p-0.5"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{liveChannel.name}</p>
                <p className="text-gray-500 text-xs truncate">{liveChannel.category}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LiveTVMiniPlayer;
